<!--
// app/components/pages/region-stats/StatsDataTable.vue
-->
<script setup lang="ts">
import type { RankingsData, TurnMvpPlayer } from "~/src/player/domain/Player";
import type { GroupedRanking } from "~/src/stats/domain/models/Rankings";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import PrimeDataTable from "primevue/datatable";
import PrimeColumn from "primevue/column";
import CardNoDataMessage from "~/app/components/Shared/CardNoDataMessage.vue";

type Props = {
  mainStat: string;
  tableData: RankingsData[] | GroupedRanking[] | TurnMvpPlayer[];
  dataFields: {
    ranking: string;
    name: string;
    sortField: string;
    stats: [{ stat: string; header: string; sortable: boolean; tooltip: string }];
  };
  initialStyleMainCol?: boolean;
  isTurnStat?: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits(["sort"]);

const route = useRoute();
const localePath = useLocalePath();
const { t } = useI18n();

const constructUrl = (rankingData: RankingsData, field: string): string => {
  if (field === "player") {
    return localePath({
      name: "players-playerId-playerName",
      params: {
        playerId: rankingData.player.id,
        playerName: slugify(rankingData.player.shortName),
      },
    });
  } else if (field === "team") {
    return localePath({
      name: "teams-team-teamName",
      params: {
        team: rankingData.team.id,
        teamName: slugify(rankingData.team.completeName),
      },
    });
  } else {
    return "";
  }
};
</script>

<template>
  <PrimeDataTable
    :value="tableData"
    scrollable
    scrollHeight="640px"
    :default-sort-order="-1"
    :null-sort-order="0"
    :sort-field="dataFields.sortField"
    :sort-order="-1"
    @sort="e => emit('sort', e)"
    lazy
  >
    <PrimeColumn
      :field="dataFields.ranking"
      :header="!isTurnStat ? 'Ranking' : $t('general.matchday.word')"
      :frozen="!isTurnStat"
      align-frozen="left"
      :class="isTurnStat ? 'ranking-turn' : 'ranking'"
      :style="isTurnStat ? 'min-width: 80px;' : 'min-width: 50px; padding-inline-end: 5px;'"
    >
      <template v-if="isTurnStat" #body="{ data, field }">
        <span :class="{ '!text-xs': !!data && data?.turnName && Number(isNaN(data?.turnName)) }">{{
          data?.turnName ? data?.turnName : ""
        }}</span>
      </template>
    </PrimeColumn>
    <PrimeColumn
      :field="dataFields.name"
      :header="$t('cards.stats.name.text')"
      class="player-name"
      style="min-width: 160px"
      :frozen="!isTurnStat"
      align-frozen="left"
    >
      <template #body="{ data, field }">
        <h2>
          <nuxt-link
            :to="{ path: constructUrl(data, field) }"
            v-text="
              field === 'player' ? data?.player.shortName : field === 'team' ? data?.team.completeName : ''
            "
          >
          </nuxt-link>
        </h2>
      </template>
    </PrimeColumn>
    <PrimeColumn
      field="team"
      :header="$t('tables.team.word')"
      class="team"
      style="min-width: 50px; text-align: center"
    >
      <template #body="{ data }">
        <div class="flex items-center justify-center">
          <NuxtImg
            v-if="data && data?.team"
            :src="setAliasUrl(data?.team?.logo?.url)"
            :alt="data?.team?.completeName"
            loading="lazy"
            width="48"
            height="48"
            class="team-logo"
            sizes="40px sm:48px"
          />
          <NuxtImg
            v-else-if="data && data?.playerSeasonInfo"
            :src="setAliasUrl(data?.playerSeasonInfo?.team?.logo?.url)"
            :alt="data?.playerSeasonInfo?.team?.completeName"
            loading="lazy"
            width="48"
            height="48"
            class="team-logo"
          />
          <div
            v-else
            class="w-10 h-10 md:w-12 md:h-12 bg-gray-700 border-2 border-gray-800 rounded-full"
          ></div>
        </div>
      </template>
    </PrimeColumn>
    <PrimeColumn
      field="0"
      style="max-width: 500px; min-width: 40px; width: 80%"
      :class="{ 'separator-col': !isTurnStat }"
    ></PrimeColumn>
    <PrimeColumn
      v-if="!isTurnStat"
      v-for="field in dataFields.stats"
      :field="field.stat"
      class="data-column"
      style="min-width: 60px"
      :sortable="field.sortable"
    >
      <template #header>
        <span class="p-column-title" v-tooltip.top="field.tooltip">
          {{ field.header }}
        </span>
      </template>
    </PrimeColumn>
  </PrimeDataTable>
  <card-no-data-message
    v-if="!tableData || !tableData.length"
    :message="$t('stats.message.notAvailable')"
  ></card-no-data-message>
</template>

<style scoped></style>
